// #region Imports
import { createAsyncThunk, createSelector, createSlice, SerializedError } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import BookmarkAPIService from "marketplace/bookmarks/_api/bookmark-api-service";
import { BookmarkedCompanyJSON } from "marketplace/bookmarks/company-bookmarks/_schema/bookmarked-company-schema";
import BookmarkedCompany from "marketplace/bookmarks/company-bookmarks/bookmarked-company";
import { BookmarkedIndividualJSON } from "marketplace/bookmarks/individual-bookmarks/_schema/bookmarked-individual-schema";
import BookmarkedIndividual from "marketplace/bookmarks/individual-bookmarks/bookmarked-individual";
import { BookmarkedTeamJSON } from "marketplace/bookmarks/team-bookmarks/_schema/bookmarked-team-schema";
import BookmarkedTeam from "marketplace/bookmarks/team-bookmarks/bookmarked-team";
import Session from "users/__session/session";
// #endregion

// #region Store State Type
export type BookmarksState = {
  companyBookmarks: {
    entries?: BookmarkedCompanyJSON[];
    loading: boolean;
    error: SerializedError | null;
  };
  individualBookmarks: {
    entries?: BookmarkedIndividualJSON[];
    loading: boolean;
    error: SerializedError | null;
  };
  teamBookmarks: {
    entries?: BookmarkedTeamJSON[];
    loading: boolean;
    error: SerializedError | null;
  };
  loading: boolean;
  error: SerializedError | null;
};
// #endregion

// #region Initial State
const initialState: BookmarksState = {
  companyBookmarks: {
    entries: undefined,
    loading: false,
    error: null,
  },
  individualBookmarks: {
    entries: undefined,
    loading: false,
    error: null,
  },
  teamBookmarks: {
    entries: undefined,
    loading: false,
    error: null,
  },
  loading: false,
  error: null,
};
// #endregion

// #region Async Thunks
export const populateAllBookmarksForUser = createAsyncThunk(
  "bookmarks/populateAllBookmarksForUser",
  async ({ session }: { session: Session }, thunkAPI) => {
    try {
      const apiService = new BookmarkAPIService(session);
      return await apiService.getAllBookmarksForUser();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const populateCompanyBookmarksForUser = createAsyncThunk(
  "bookmarks/populateAllCompanyBookmarksForUser",
  async ({ session }: { session: Session }, thunkAPI) => {
    try {
      const apiService = new BookmarkAPIService(session);
      return await apiService.getAllCompanyBookmarksForUser();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const populateIndividualBookmarksForUser = createAsyncThunk(
  "bookmarks/populateAllIndividualBookmarksForUser",
  async ({ session }: { session: Session }, thunkAPI) => {
    try {
      const apiService = new BookmarkAPIService(session);
      return await apiService.getAllIndividualBookmarksForUser();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const populateTeamBookmarksForUser = createAsyncThunk(
  "bookmarks/populateAllTeamBookmarksForUser",
  async ({ session }: { session: Session }, thunkAPI) => {
    try {
      const apiService = new BookmarkAPIService(session);
      return await apiService.getAllTeamBookmarksForUser();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const createTeamBookmark = createAsyncThunk(
  "bookmarks/createTeamBookmark",
  async ({ session, teamId }: { session: Session; teamId: Guid }, thunkAPI) => {
    try {
      const apiService = new BookmarkAPIService(session);
      return await apiService.createTeamBookmark(teamId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const deleteTeamBookmark = createAsyncThunk(
  "bookmarks/deleteTeamBookmark",
  async ({ session, bookmarkId }: { session: Session; bookmarkId: Guid }, thunkAPI) => {
    try {
      const apiService = new BookmarkAPIService(session);
      await apiService.deleteTeamBookmark(bookmarkId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const createCompanyBookmark = createAsyncThunk(
  "bookmarks/createCompanyBookmark",
  async ({ session, companyId }: { session: Session; companyId: Guid }, thunkAPI) => {
    try {
      const apiService = new BookmarkAPIService(session);
      return await apiService.createCompanyBookmark(companyId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const deleteCompanyBookmark = createAsyncThunk(
  "bookmarks/deleteCompanyBookmark",
  async ({ session, bookmarkId }: { session: Session; bookmarkId: Guid }, thunkAPI) => {
    try {
      const apiService = new BookmarkAPIService(session);
      await apiService.deleteCompanyBookmark(bookmarkId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const createIndividualBookmark = createAsyncThunk(
  "bookmarks/createIndividualBookmark",
  async ({ session, individualId }: { session: Session; individualId: Guid }, thunkAPI) => {
    try {
      const apiService = new BookmarkAPIService(session);
      return await apiService.createIndividualBookmark(individualId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const deleteIndividualBookmark = createAsyncThunk(
  "bookmarks/deleteIndividualBookmark",
  async ({ session, bookmarkId }: { session: Session; bookmarkId: Guid }, thunkAPI) => {
    try {
      const apiService = new BookmarkAPIService(session);
      return await apiService.deleteIndividualBookmark(bookmarkId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);
// #endregion

// #region Slice
const bookmarksSlice = createSlice({
  name: "bookmarks",
  initialState,
  // #region Simple Reducers
  reducers: {
    addCompanyBookmark: (state, action) => {
      if (!state.companyBookmarks.entries) {
        state.companyBookmarks.entries = [];
      }
      state.companyBookmarks.entries.push(action.payload);
    },
    removeCompanyBookmark: (state, action) => {
      if (!state.companyBookmarks.entries) {
        return;
      }
      state.companyBookmarks.entries = state.companyBookmarks.entries.filter(
        (bookmark) => bookmark.id !== action.payload.id.value,
      );
    },
    addIndividualBookmark: (state, action) => {
      if (!state.individualBookmarks.entries) {
        state.individualBookmarks.entries = [];
      }
      state.individualBookmarks.entries.push(action.payload);
    },
    removeIndividualBookmark: (state, action) => {
      if (!state.individualBookmarks.entries) {
        return;
      }
      state.individualBookmarks.entries = state.individualBookmarks.entries.filter(
        (bookmark) => bookmark.id !== action.payload.id.value,
      );
    },
    addTeamBookmark: (state, action) => {
      if (!state.teamBookmarks.entries) {
        state.teamBookmarks.entries = [];
      }
      state.teamBookmarks.entries.push(action.payload);
    },
    removeTeamBookmark: (state, action) => {
      if (!state.teamBookmarks.entries) {
        return;
      }
      state.teamBookmarks.entries = state.teamBookmarks.entries.filter(
        (bookmark) => bookmark.id !== action.payload.id.value,
      );
    },
  },
  // #region Async Reducers
  extraReducers: (builder) => {
    // createTeamBookmark
    builder.addCase(createTeamBookmark.fulfilled, (state, action) => {
      if (!state.teamBookmarks.entries) {
        state.teamBookmarks.entries = [];
      }
      state.teamBookmarks.entries.push(action.payload);
    });

    builder.addCase(deleteTeamBookmark.fulfilled, (state, action) => {
      if (!state.teamBookmarks.entries) {
        return;
      }
      state.teamBookmarks.entries = state.teamBookmarks.entries.filter(
        (bookmark) => bookmark.id !== action.meta.arg.bookmarkId.value,
      );
    });

    // createCompanyBookmark
    builder.addCase(createCompanyBookmark.fulfilled, (state, action) => {
      if (!state.companyBookmarks.entries) {
        state.companyBookmarks.entries = [];
      }
      state.companyBookmarks.entries.push(action.payload);
    });

    // deleteCompanyBookmark
    builder.addCase(deleteCompanyBookmark.fulfilled, (state, action) => {
      if (!state.companyBookmarks.entries) {
        return;
      }
      state.companyBookmarks.entries = state.companyBookmarks.entries.filter(
        (bookmark) => bookmark.id !== action.meta.arg.bookmarkId.value,
      );
    });

    // createIndividualBookmark
    builder.addCase(createIndividualBookmark.fulfilled, (state, action) => {
      if (!state.individualBookmarks.entries) {
        state.individualBookmarks.entries = [];
      }
      state.individualBookmarks.entries.push(action.payload);
    });

    // deleteIndividualBookmark
    builder.addCase(deleteIndividualBookmark.fulfilled, (state, action) => {
      if (!state.individualBookmarks.entries) {
        return;
      }
      state.individualBookmarks.entries = state.individualBookmarks.entries.filter(
        (bookmark) => bookmark.id !== action.meta.arg.bookmarkId.value,
      );
    });

    // populateAllBookmarksForUser
    builder.addCase(populateAllBookmarksForUser.pending, (state, _action) => {
      state.loading = true;
      state.error = null;

      state.companyBookmarks.loading = true;
      state.companyBookmarks.error = null;

      state.individualBookmarks.loading = true;
      state.individualBookmarks.error = null;

      state.teamBookmarks.loading = true;
      state.teamBookmarks.error = null;
    });
    builder.addCase(populateAllBookmarksForUser.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;

      state.companyBookmarks.entries = action.payload.company;
      state.companyBookmarks.loading = false;
      state.companyBookmarks.error = null;

      state.individualBookmarks.entries = action.payload.individual;
      state.individualBookmarks.loading = false;
      state.individualBookmarks.error = null;

      state.teamBookmarks.entries = action.payload.team;
      state.teamBookmarks.loading = false;
      state.teamBookmarks.error = null;
    });
    builder.addCase(populateAllBookmarksForUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;

      state.companyBookmarks.loading = false;
      state.companyBookmarks.error = action.error;

      state.individualBookmarks.loading = false;
      state.individualBookmarks.error = action.error;

      state.teamBookmarks.loading = false;
      state.teamBookmarks.error = action.error;
    });

    // populateCompanyBookmarksForUser
    builder.addCase(populateCompanyBookmarksForUser.pending, (state, _action) => {
      state.companyBookmarks.loading = true;
      state.companyBookmarks.error = null;
    });
    builder.addCase(populateCompanyBookmarksForUser.fulfilled, (state, action) => {
      state.companyBookmarks.entries = action.payload;
      state.companyBookmarks.loading = false;
      state.companyBookmarks.error = null;
    });
    builder.addCase(populateCompanyBookmarksForUser.rejected, (state, action) => {
      state.companyBookmarks.loading = false;
      state.companyBookmarks.error = action.error;
    });

    // populateIndividualBookmarksForUser
    builder.addCase(populateIndividualBookmarksForUser.pending, (state, _action) => {
      state.individualBookmarks.loading = true;
      state.individualBookmarks.error = null;
    });
    builder.addCase(populateIndividualBookmarksForUser.fulfilled, (state, action) => {
      state.individualBookmarks.entries = action.payload;
      state.individualBookmarks.loading = false;
      state.individualBookmarks.error = null;
    });
    builder.addCase(populateIndividualBookmarksForUser.rejected, (state, action) => {
      state.individualBookmarks.loading = false;
      state.individualBookmarks.error = action.error;
    });

    // populateTeamBookmarksForUser
    builder.addCase(populateTeamBookmarksForUser.pending, (state, _action) => {
      state.teamBookmarks.loading = true;
      state.teamBookmarks.error = null;
    });
    builder.addCase(populateTeamBookmarksForUser.fulfilled, (state, action) => {
      state.teamBookmarks.entries = action.payload;
      state.teamBookmarks.loading = false;
      state.teamBookmarks.error = null;
    });
    builder.addCase(populateTeamBookmarksForUser.rejected, (state, action) => {
      state.teamBookmarks.loading = false;
      state.teamBookmarks.error = action.error;
    });
  },
});
// #endregion

// #region Exports ------------------------------------------------------------

export const {
  addCompanyBookmark,
  removeCompanyBookmark,
  addIndividualBookmark,
  removeIndividualBookmark,
  addTeamBookmark,
  removeTeamBookmark,
} = bookmarksSlice.actions;

// #region Team Bookmarks

// getTeamBookmarkByTeamId ----------------------------------------------------
const selectTeamBookmarkById = createSelector(
  [(s: RootState) => s.bookmarks.teamBookmarks.entries, (_: RootState, id?: Guid) => id?.value],
  (entries, id) => {
    if (!id) return;
    if (!entries) return;
    const entry = entries.find((bookmark) => bookmark.teamId === id);
    if (!entry) return;
    return BookmarkedTeam.fromJSON(entry);
  },
);
export const getTeamBookmarkByTeamId = (id?: Guid) => (state: RootState) => selectTeamBookmarkById(state, id);
// ----------------------------------------------------------------------------

// getTeamBookmarks -----------------------------------------------------------
const selectTeamBookmarks = createSelector([(state: RootState) => state.bookmarks.teamBookmarks.entries], (entries) => {
  if (!entries) return;
  return BookmarkedTeam.fromJSONArray(entries);
});
export const getTeamBookmarks = () => (state: RootState) => selectTeamBookmarks(state);
// ----------------------------------------------------------------------------

export const getIsLoadingTeamBookmarks = () => (state: RootState) => state.bookmarks.teamBookmarks.loading;

export const getErrorLoadingTeamBookmarks = () => (state: RootState) => state.bookmarks.teamBookmarks.error;

// #endregion

// #region Company Bookmarks

// getCompanyBookmarkByCompanyId ----------------------------------------------
const selectCompanyBookmarkById = createSelector(
  [(s: RootState) => s.bookmarks.companyBookmarks.entries, (_: RootState, id?: Guid) => id?.value],
  (entries, companyIdValue) => {
    if (!companyIdValue) return;
    if (!entries) return;
    const entry = entries.find((bookmark) => bookmark.companyId === companyIdValue);
    if (!entry) return;
    return BookmarkedCompany.fromJSON(entry);
  },
);
export const getCompanyBookmarkByCompanyId = (id?: Guid) => (state: RootState) => selectCompanyBookmarkById(state, id);
// ----------------------------------------------------------------------------

// #region getIsLoadingCompanyBookmarkByCompanyId -----------------------------
const selectIsLoadingCompanyBookmarkByCompanyId = createSelector(
  [
    (s: RootState) => s.bookmarks.companyBookmarks.loading,
    (s: RootState) => s.bookmarks.companyBookmarks.entries,
    (_: RootState, companyId?: Guid) => companyId?.value,
  ],
  (loading, entries, companyIdValue) =>
    !!companyIdValue && loading && entries && !entries.some((b) => b.companyId === companyIdValue),
);
export const getIsLoadingCompanyBookmarkByCompanyId = (companyId?: Guid) => (state: RootState) =>
  selectIsLoadingCompanyBookmarkByCompanyId(state, companyId);
// ----------------------------------------------------------------------------

// getCompanyBookmarks --------------------------------------------------------
const selectGetCompanyBookmarks = createSelector(
  [(state: RootState) => state.bookmarks.companyBookmarks.entries],
  (entries) => {
    if (!entries) return;
    return BookmarkedCompany.fromJSONArray(entries);
  },
);
export const getCompanyBookmarks = () => (state: RootState) => selectGetCompanyBookmarks(state);
// ----------------------------------------------------------------------------

export const getIsLoadingCompanyBookmarks = () => (state: RootState) => state.bookmarks.companyBookmarks.loading;

export const getErrorLoadingCompanyBookmarks = () => (state: RootState) => state.bookmarks.companyBookmarks.error;

// #endregion

// #region Individual Bookmarks

// getIndividualBookmarkByIndividualId ----------------------------------------
const selectIndividualBookmarkByIndividualId = createSelector(
  [(s: RootState) => s.bookmarks.individualBookmarks.entries, (_: RootState, id?: Guid) => id?.value],
  (entries, individualIdValue) => {
    if (!individualIdValue) return;
    const entry = entries?.find((b) => b.individualId === individualIdValue);
    if (!entry) return;
    return BookmarkedIndividual.fromJSON(entry);
  },
);
export const getIndividualBookmarkByIndividualId = (individualId?: Guid) => (state: RootState) =>
  selectIndividualBookmarkByIndividualId(state, individualId);
// ----------------------------------------------------------------------------

// getIsLoadingIndividualBookmarkByIndividualId -------------------------------
const selectIsLoadingIndividualBookmarkByIndividualId = createSelector(
  [
    (s: RootState) => s.bookmarks.individualBookmarks.loading,
    (s: RootState) => s.bookmarks.individualBookmarks.entries,
    (_: RootState, individualId?: Guid) => individualId?.value,
  ],
  (loading, entries, individualIdValue) =>
    !!individualIdValue && loading && entries && !entries.some((b) => b.individualId === individualIdValue),
);
export const getIsLoadingIndividualBookmarkByIndividualId = (individualId?: Guid) => (state: RootState) =>
  selectIsLoadingIndividualBookmarkByIndividualId(state, individualId);
// ----------------------------------------------------------------------------

// getIndividualBookmarks --------------------------------------------------------
const selectGetIndividualBookmarks = createSelector(
  [(state: RootState) => state.bookmarks.individualBookmarks.entries],
  (entries) => {
    if (!entries) return;
    return BookmarkedIndividual.fromJSONArray(entries);
  },
);
export const getIndividualBookmarks = () => (state: RootState) => selectGetIndividualBookmarks(state);
// ----------------------------------------------------------------------------

export const getIsLoadingIndividualBookmarks = () => (state: RootState) => state.bookmarks.individualBookmarks.loading;

export const getErrorLoadingIndividualBookmarks = () => (state: RootState) => state.bookmarks.individualBookmarks.error;

export const getIsLoadingAnyBookmarks = () => (state: RootState) => state.bookmarks.loading;

export const getErrorLoadingAnyBookmarks = () => (state: RootState) => state.bookmarks.error;
// #endregion

export default bookmarksSlice;
